/* GASSISA
 =========================================================================================
 * Descripción: Generadores ESC/POS del formato clásico de tickets.
 *
 *              Estos generadores preservan la salida anterior al diseñador de tickets.
 *              Se usan únicamente cuando no existe un esquema por objetos configurado.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1        | 24/10/2026 | José Luis Aragón Cervantes       | Recupera formato clásico como respaldo sin esquema
 * ---------------------------------------------------------------------------------------*/

const encodeTicketDatoClave = (textEncoder, texto) => (
    textEncoder.encode("\x1B\x45\x01\x1B\x21\x10" + texto + "\x1B\x21\x00\x1B\x45\x00")
);

const numeroTicket = (valor) => Number(valor) || 0;

export const buildTicketVentaClasicoParts = ({
    dataTicket = {},
    parametrosCajaForm = {},
    esReimpresion = false,
}) => {
    const textEncoder = new TextEncoder();
    const cabecero = dataTicket.cabecero || {};
    const detalles = dataTicket.detalles || [];
    const partes = [
        textEncoder.encode("\x1B\x40"),
        textEncoder.encode("\x1B\x4B\x60"),
    ];

    partes.push(
        textEncoder.encode("\x1B\x33\x00"),
        textEncoder.encode("\x1B\x61\x01\x1B\x21\x00" + (dataTicket.encabezado || "").trim() + "\n"),
        textEncoder.encode("\x1B\x32"),
        textEncoder.encode("\x1B\x61\x00\x1B\x21\x00" + "-".repeat(32) + "\n"),
        textEncoder.encode("Caja: " + (cabecero.pc || "") + "\n"),
        encodeTicketDatoClave(textEncoder, "Remisión: " + (cabecero.remision || "") + "\n"),
        textEncoder.encode("Vendedor: " + (cabecero.usuario || "") + "\n"),
        encodeTicketDatoClave(textEncoder, "Fecha: " + (cabecero.fechaventa || "") + "\n"),
        textEncoder.encode("Tipo de Pago: " + (cabecero.tipopago || "") + "\n"),
        encodeTicketDatoClave(textEncoder, "Cliente: " + (cabecero.razonsocial || "No especificado") + "\n"),
        textEncoder.encode("-".repeat(32) + "\n")
    );

    detalles.forEach((articulo) => {
        partes.push(textEncoder.encode("\x1B\x45\x01" + (articulo.descripcion || "Sin descripción") + "\x1B\x45\x00" + "\n"));
        partes.push(textEncoder.encode("Cantidad: " + (articulo.cantidad || 0) + " - Talla: " + (articulo.medida || 0) + "\n"));
        partes.push(textEncoder.encode("P.Unit: $" + numeroTicket(articulo.costounitario).toFixed(2) + "  Desc: $" + numeroTicket(articulo.descuento).toFixed(2) + "\n"));
        partes.push(textEncoder.encode("Importe: $" + numeroTicket(articulo.total).toFixed(2) + "\n"));
        partes.push(textEncoder.encode("Vendedor: " + ((articulo.vendedorpreventa || "").trim() || "N/A") + "\n"));
        partes.push(textEncoder.encode("Origen: " + ((articulo.almacenexhibidor || "").trim() || "N/A") + "\n"));
        partes.push(textEncoder.encode(".".repeat(32) + "\n"));
    });

    const folioVale = numeroTicket(cabecero.foliovaleporcambio ?? cabecero.FolioValePorCambio ?? cabecero.foliovale);
    const montoVale = numeroTicket(cabecero.montovaleporcambio ?? cabecero.MontoValePorCambio ?? cabecero.montovale);
    const hayVale = folioVale > 0 && montoVale > 0;

    partes.push(
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("\x1B\x61\x00Subtotal: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.subtotal).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00IVA: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.iva).toFixed(2) + "\n")
    );

    if (hayVale) {
        partes.push(
            textEncoder.encode("\x1B\x61\x00Folio Vale: "),
            textEncoder.encode("\x1B\x61\x02" + folioVale + "\n"),
            textEncoder.encode("\x1B\x61\x00Descto Vale: "),
            textEncoder.encode("\x1B\x61\x02$" + montoVale.toFixed(2) + "\n")
        );
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x00Total: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.total).toFixed(2) + "\n")
    );

    if ((cabecero.tipopago || "").trim() !== "EFECTIVO") {
        partes.push(
            textEncoder.encode("\x1B\x61\x00Total tipo pago: "),
            textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.totaltipopago).toFixed(2) + "\n")
        );
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x00Efectivo: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.totalefectivo).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00Cambio: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(cabecero.totalcambio).toFixed(2) + "\n"),
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("\x1B\x61\x01\x1B\x45\x01POLÍTICA DE CAMBIOS (15 DÍAS)\n\x1B\x45\x00"),
        textEncoder.encode("\x1B\x61\x00Ticket físico indispensable.\n"),
        textEncoder.encode("Artículos sin uso, con etiquetas\ny empaque original.\n"),
        textEncoder.encode("No aplica en ofertas. Cambios\nsolo por igual o mayor valor.\n"),
        textEncoder.encode("\x1B\x45\x01NO SE ACEPTAN DEVOLUCIONES.\n\x1B\x45\x00\n"),
        textEncoder.encode("\x1B\x61\x00" + "-".repeat(32) + "\n")
    );

    const pieConfigurado = parametrosCajaForm.pieticket || "";
    if (pieConfigurado.length > 0) {
        partes.push(textEncoder.encode("\x1B\x61\x01" + pieConfigurado + "\n"));
    }

    if (esReimpresion) {
        partes.push(textEncoder.encode("\x1B\x61\x01REIMPRESION VENTA\n"));
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x01Vuelva pronto\n\n\n\n\n"),
        textEncoder.encode("\x1D\x56\x00")
    );

    return { partes };
};

export const buildTicketApartadoClasicoParts = ({
    cabecero = {},
    detalles = [],
    totales = {},
    parametrosCajaForm = {},
    titulo = "",
    esCopiaCliente = false,
}) => {
    const textEncoder = new TextEncoder();
    const partes = [
        textEncoder.encode("\x1B\x40"),
        textEncoder.encode("\x1B\x4B\x30"),
    ];
    const conceptoApartado = (cabecero.concepto || "").trim();
    const grupoApartado = (cabecero.grupo || "").trim();

    partes.push(textEncoder.encode("\x1B\x33\x00"));
    partes.push(textEncoder.encode("\x1B\x61\x01\x1B\x21\x00" + (titulo || "").trim() + "\n"));
    partes.push(textEncoder.encode("\x1B\x32"));
    partes.push(textEncoder.encode("\x1B\x61\x00\x1B\x21\x00" + "-".repeat(32) + "\n"));

    const encabezado = (parametrosCajaForm.encabezadoticket || "").trim();
    if (encabezado.length > 0) {
        partes.push(textEncoder.encode("\x1B\x61\x01" + encabezado + "\n"));
        partes.push(textEncoder.encode("\x1B\x61\x00\x1B\x21\x00" + "-".repeat(32) + "\n"));
    }

    partes.push(
        textEncoder.encode("Caja: " + (cabecero.pc || "") + "\n"),
        encodeTicketDatoClave(textEncoder, "Apartado: " + (cabecero.folioapartado || "") + "\n"),
        textEncoder.encode("Vendedor: " + (cabecero.usuario || "") + "\n"),
        encodeTicketDatoClave(textEncoder, "Fecha: " + (cabecero.fecharegistro || "") + "\n"),
        textEncoder.encode("Tipo de Pago: APARTADO\n"),
        encodeTicketDatoClave(textEncoder, "Cliente: " + (cabecero.nombrecliente || "No especificado") + "\n")
    );
    if (conceptoApartado.length > 0) {
        partes.push(encodeTicketDatoClave(textEncoder, "Concepto: " + conceptoApartado + "\n"));
    }
    if (grupoApartado.length > 0) {
        partes.push(encodeTicketDatoClave(textEncoder, "Grupo: " + grupoApartado + "\n"));
    }
    partes.push(textEncoder.encode("-".repeat(32) + "\n"));

    detalles.forEach((articulo) => {
        partes.push(textEncoder.encode("\x1B\x45\x01" + (articulo.descripcion || "") + "\x1B\x45\x00\n"));
        partes.push(textEncoder.encode("Cantidad: " + (articulo.cantidad || 0) + " - Talla: " + (articulo.medida || 0) + "\n"));
        partes.push(textEncoder.encode("P.Unit: $" + numeroTicket(articulo.pUnit).toFixed(2) + "  Desc: $" + numeroTicket(articulo.desc).toFixed(2) + "\n"));
        partes.push(textEncoder.encode("Importe: $" + numeroTicket(articulo.importeVenta).toFixed(2) + "\n"));
        partes.push(textEncoder.encode("Abono: $" + numeroTicket(articulo.abono).toFixed(2) + "\n"));
        if (articulo.llevaIVA) {
            partes.push(textEncoder.encode("IVA abono: $" + numeroTicket(articulo.ivaPartida).toFixed(2) + "\n"));
        }
        partes.push(textEncoder.encode("Vendedor: " + (articulo.vendedorpreventa || "N/A") + "\n"));
        partes.push(textEncoder.encode("Origen: " + (articulo.almacenexhibidor || "N/A") + "\n"));
        partes.push(textEncoder.encode(".".repeat(32) + "\n"));
    });

    partes.push(
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("\x1B\x61\x00Subtotal abonos: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(totales.subTotal).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00IVA abonos: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(totales.totalIVA).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00Total articulos: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(totales.totalImporteArticulos).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00Total abonado: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(totales.totalAbonado).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00Restante: "),
        textEncoder.encode("\x1B\x61\x02$" + numeroTicket(totales.totalImporteArticulos - totales.totalAbonado).toFixed(2) + "\n"),
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("Total en letra:\n"),
        textEncoder.encode((cabecero.montoabonoletra || "") + "\n"),
        textEncoder.encode("-".repeat(32) + "\n")
    );

    if (esCopiaCliente) {
        partes.push(
            textEncoder.encode("-".repeat(32) + "\n"),
            textEncoder.encode("\x1B\x61\x01\x1B\x45\x01AVISO IMPORTANTE:\n\x1B\x45\x00"),
            textEncoder.encode("\x1B\x61\x00Vigencia apartado: 15 dias.\n"),
            textEncoder.encode("Ticket fisico indispensable.\n"),
            textEncoder.encode("\x1B\x45\x01NO SE ACEPTAN DEVOLUCIONES.\n\x1B\x45\x00"),
            textEncoder.encode("Articulos sin uso y c/etiqueta.\n"),
            textEncoder.encode("No aplica en ofertas/promociones.\n"),
            textEncoder.encode("Conserve este ticket fisico.\n"),
            textEncoder.encode("-".repeat(32) + "\n")
        );
    }

    const pieConfigurado = parametrosCajaForm.pieticket || "";
    if (pieConfigurado.length > 0) {
        partes.push(textEncoder.encode("\x1B\x61\x01" + pieConfigurado + "\n"));
        partes.push(textEncoder.encode("\x1B\x61\x00\x1B\x21\x00" + "-".repeat(32) + "\n"));
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x01Gracias por su compra!\nVuelva pronto\n\n\n"),
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("\n\n\n\n\n"),
        textEncoder.encode("\x1D\x56\x00")
    );

    return { partes };
};

// Formato histórico de cotización para reimpresión. No consulta ticketLayout:
// se invoca como respaldo cuando la caja no tiene un esquema en Parametros.
export const buildTicketCotizacionClasicoParts = ({
    dataTicket = {},
    folioCotizacion = "",
    caja = "",
}) => {
    const textEncoder = new TextEncoder();
    const cabecero = dataTicket.cabecero || {};
    const detalles = dataTicket.detalles || [];
    const partes = [textEncoder.encode("\x1B\x40")];
    let subtotal = 0;
    let totalIVA = 0;
    let total = 0;

    partes.push(
        textEncoder.encode("\x1B\x61\x01\x1B\x21\x10C O T I Z A C I O N\n"),
        textEncoder.encode("\x1B\x61\x00\x1B\x21\x00" + "-".repeat(32) + "\n"),
        textEncoder.encode("Folio: " + folioCotizacion + "\n"),
        textEncoder.encode("Caja: " + caja + "\n"),
        textEncoder.encode("Fecha: " + (cabecero.fecharegistro || cabecero.fechaventa || "") + "\n")
    );

    if (cabecero.razonsocial) {
        partes.push(textEncoder.encode("Cliente: " + cabecero.razonsocial + "\n"));
    }
    partes.push(textEncoder.encode("-".repeat(32) + "\n\n"));

    detalles.forEach((item) => {
        const descripcion = (item.descripcion || "").substring(0, 20);
        const cantidad = Number(item.cantidad) || 0;
        const precio = Number(item.costocliente ?? item.costounitario) || 0;
        const importe = cantidad * precio;
        subtotal += importe;
        totalIVA += Number(item.iva) || 0;
        total += Number(item.total) || 0;
        partes.push(textEncoder.encode(
            descripcion + "\n" + cantidad + " x $" + precio.toFixed(2) + " = $" + importe.toFixed(2) + "\n"
        ));
    });

    partes.push(
        textEncoder.encode("-".repeat(32) + "\n"),
        textEncoder.encode("Subtotal: $" + subtotal.toFixed(2) + "\n"),
        textEncoder.encode("IVA: $" + totalIVA.toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x21\x11TOTAL: $" + total.toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x21\x00"),
        textEncoder.encode("\n\n"),
        textEncoder.encode("\x1B\x61\x01REIMPRESION COTIZACION\n\n\n"),
        textEncoder.encode("\x1B\x64\x06"),
        textEncoder.encode("\x1D\x56\x00")
    );

    return { partes };
};
